// ---------------------------------------------------------------------------
// Hip-spine X-ray landmark names (mirrors hip_spine_ai/ai/landmark_names.py)
// ---------------------------------------------------------------------------

import type { HipSpineLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import type { HipSpinePostureApiObject } from '@/api/hip-spine-study/hipSpinePosture';

export const HipSpineLandmarkName = {
    SacrumAnterior: 'sacrum_anterior',
    SacrumPosterior: 'sacrum_posterior',
    L5Anterior: 'L5_anterior',
    L5Posterior: 'L5_posterior',
    L4Anterior: 'L4_anterior',
    L4Posterior: 'L4_posterior',
    L3Anterior: 'L3_anterior',
    L3Posterior: 'L3_posterior',
    L2Anterior: 'L2_anterior',
    L2Posterior: 'L2_posterior',
    L1Anterior: 'L1_anterior',
    L1Posterior: 'L1_posterior',
    HJC1: 'HJC_1',
    AcetabulumAnterior1: 'acetabulum_anterior_1',
    AcetabulumPosterior1: 'acetabulum_posterior_1',
    FemurCortex1: 'femur_cortex_1',
    HJC2: 'HJC_2',
    AcetabulumAnterior2: 'acetabulum_anterior_2',
    AcetabulumPosterior2: 'acetabulum_posterior_2',
    FemurCortex2: 'femur_cortex_2',
    ASIS1: 'asis_1',
    ASIS2: 'asis_2',
    PubicSymphysis: 'pubic_symphysis',
} as const;

type HipSpineLandmarkNameValue = (typeof HipSpineLandmarkName)[keyof typeof HipSpineLandmarkName];

export type HipSpineLandmark = { name: HipSpineLandmarkNameValue };

export const LUMBAR_LEVELS = ['L1', 'L2', 'L3', 'L4', 'L5'] as const;
export type LumbarLevel = (typeof LUMBAR_LEVELS)[number];

export interface LumbarPair {
    readonly level: LumbarLevel;
    readonly anterior: string;
    readonly posterior: string;
}

/**
 * Lumbar endplate pairs ordered from highest (L1) to lowest (L5). Each pair
 * carries the level name plus the two landmark names so callers can name the
 * level for messaging without re-parsing strings.
 */
export const LUMBAR_LEVEL_PAIRS: readonly LumbarPair[] = LUMBAR_LEVELS.map((level) => ({
    level,
    anterior: `${level}_anterior`,
    posterior: `${level}_posterior`,
}));

const ALL_LUMBAR_NAMES = new Set<string>(
    LUMBAR_LEVEL_PAIRS.flatMap((pair) => [pair.anterior, pair.posterior]),
);

export interface HighestLumbarSelection {
    /**
     * The chosen lumbar pair, or null when no candidate pair is fully present
     * in the input (e.g. the fallback level's landmarks weren't supplied).
     */
    pair: LumbarPair | null;
    /**
     * The input array filtered to keep all non-lumbar landmarks plus only the
     * chosen pair's two lumbar landmarks.
     */
    landmarks: HipSpineLandmarkApiObject[];
}

/**
 * Prune the lumbar set to a specific level's anterior/posterior pair, keeping
 * all non-lumbar landmarks. Returns `pair = null` when the requested level's
 * landmarks are not both present.
 */
export function selectLumbarPair(
    landmarks: HipSpineLandmarkApiObject[],
    level: LumbarLevel,
): HighestLumbarSelection {
    const byName = new Map(landmarks.map((landmark) => [landmark.name, landmark]));
    const chosen =
        LUMBAR_LEVEL_PAIRS.find((candidate) => candidate.level === level) ?? LUMBAR_LEVEL_PAIRS[0];
    const pair = byName.has(chosen.anterior) && byName.has(chosen.posterior) ? chosen : null;
    // Keep is derived from the complete pair, not the requested level: when the
    // chosen level is incomplete (pair === null) no lumbar landmark survives, so
    // a lone present half can't leak through as an orphan dot with no partner.
    const keep = pair ? new Set<string>([pair.anterior, pair.posterior]) : new Set<string>();
    const filtered = landmarks.filter(
        (landmark) => !ALL_LUMBAR_NAMES.has(landmark.name) || keep.has(landmark.name),
    );
    return { pair, landmarks: filtered };
}

/**
 * The anterior/posterior counterpart of a landmark name, or `null` when the
 * name has no anterior/posterior designation (e.g. `HJC_1`, `asis_1`).
 * `L1_anterior ↔ L1_posterior`, `acetabulum_anterior_1 ↔ acetabulum_posterior_1`,
 * `sacrum_anterior ↔ sacrum_posterior`. Pure string mapping.
 */
export function anteriorPosteriorPartner(name: string): string | null {
    if (name.includes('anterior')) return name.replace('anterior', 'posterior');
    if (name.includes('posterior')) return name.replace('posterior', 'anterior');
    return null;
}

/**
 * Swap the anterior↔posterior designation of every landmark pair, leaving every
 * dot at its image position. Viewing a hip from the opposite side reverses which
 * physical point is anterior vs posterior, so each `*_anterior` entry takes on
 * its `*_posterior` partner's coordinates (and vice versa) while keeping its own
 * name — the rendered set of positions is unchanged, only the A/P roles swap.
 * Landmarks with no A/P partner (e.g. `HJC_1`, `asis_1`) and any pair whose
 * partner is absent are left untouched. Pure — returns a new array, mutates nothing.
 */
export function swapAnteriorPosteriorLandmarks(
    landmarks: HipSpineLandmarkApiObject[],
): HipSpineLandmarkApiObject[] {
    const byName = new Map(landmarks.map((landmark) => [landmark.name, landmark]));
    return landmarks.map((landmark) => {
        const partnerName = anteriorPosteriorPartner(landmark.name);
        const partner = partnerName ? byName.get(partnerName) : undefined;
        if (!partner) return landmark;
        return {
            ...landmark,
            original_x: partner.original_x,
            original_y: partner.original_y,
            current_x: partner.current_x,
            current_y: partner.current_y,
            likelihood: partner.likelihood,
        };
    });
}

/**
 * True when a posture's persisted state deviates from its AI-detected originals:
 * any landmark moved (current_x/y differs from original_x/y) or any editable
 * posture field (side, lumbar end plate, either femur head radius) differs from
 * its original. Pure — drives the "Reset to AI" enable state from server DTO
 * values only.
 */
export function postureDiffersFromOriginal(
    posture: HipSpinePostureApiObject,
    landmarks: ReadonlyArray<HipSpineLandmarkApiObject>,
): boolean {
    const landmarkMoved = landmarks.some(
        (l) => l.current_x !== l.original_x || l.current_y !== l.original_y,
    );
    return (
        landmarkMoved ||
        posture.side !== posture.original_side ||
        posture.lumbar_vertebra_end_plate !== posture.original_lumbar_vertebra_end_plate ||
        posture.femur_1_head_radius !== posture.original_femur_1_head_radius ||
        posture.femur_2_head_radius !== posture.original_femur_2_head_radius
    );
}
